import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { clearSigningOutState } from '../../store/slices/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../utils/constants';
import LoadingScreen from '../ui/LoadingScreen';
import ProtectedRoute from './ProtectedRoute';
import SigninScreen from '../screens/SigninScreen';
import EmailVerificationScreen from '../screens/EmailVerificationScreen';
import PersonnelNotFoundScreen from '../screens/PersonnelNotFoundScreen';
import AcknowledgementScreen from '../screens/AcknowledgementScreen';
import Dashboard from '../Dashboard';
import Profile from '../Profile';

function AppRoutes() {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  const { isLoading: auth0IsLoading, isAuthenticated: auth0IsAuthenticated } = useAuth0();
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const {
    isLoading: contextLoading,
    showEmailVerification,
    showPersonnelNotFound,
    showAcknowledgement,
    isSigningOut,
    isAuthenticated: authIsAuthenticated,
  } = authState;

  React.useEffect(() => {
    if (location.pathname === ROUTES.SIGNIN && isSigningOut) {
      const timer = setTimeout(() => {
        dispatch(clearSigningOutState());
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isSigningOut, dispatch]);

  if (contextLoading || auth0IsLoading) {
    return <LoadingScreen />;
  }

  if (isSigningOut) {
    return <LoadingScreen />;
  }

  if (showPersonnelNotFound === true) {
    return <PersonnelNotFoundScreen />;
  }
  if (auth0IsAuthenticated && showPersonnelNotFound === null) {
    return <LoadingScreen />;
  }

  // Check email verification - show email verification screen if needed
  // This should be checked even if authIsAuthenticated is false (after clearAuth)
  if (showEmailVerification === true) {
    return <EmailVerificationScreen />;
  }
  // If email verification check is still in progress (null), show loading
  if (auth0IsAuthenticated && showEmailVerification === null) {
    return <LoadingScreen />;
  }

  if (auth0IsAuthenticated) {
    const isPersonnelChecking = showPersonnelNotFound === null;
    const isAcknowledgementChecking = showAcknowledgement === null;

    if (isPersonnelChecking || isAcknowledgementChecking) {
      return <LoadingScreen />;
    }

    if (!authIsAuthenticated) {
      return <LoadingScreen />;
    }
  }

  if (showAcknowledgement === true) {
    return <AcknowledgementScreen />;
  }

  if (location.pathname === ROUTES.AUTH_CALLBACK && isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <Routes>
      <Route
        path={ROUTES.SIGNIN}
        element={
          !isAuthenticated ? (
            <SigninScreen />
          ) : (
            <Navigate to={ROUTES.HOME} replace />
          )
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.HOME}
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to={ROUTES.HOME} replace />
          ) : (
            <Navigate to={ROUTES.SIGNIN} replace />
          )
        }
      />
    </Routes>
  );
}

export default AppRoutes;
